import ToolbarPopover from '@components/FilterV3/components/ToolbarPopover/ToolbarPopover';
import BaseInput from '@components/v2/form/BaseInput/BaseInput';
import Button from '@components/v2/ui/Button/Button';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Stack, Theme } from '@mui/material';
import { Plus } from 'lucide-react';
import LangEnum from '../../../../../../lang/LangEnum';
import { useBatchUpdateCatalogSections } from '../../../../../../services/modules/catalogs/catalogs.mutations';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useCatalogSectionsDnd } from '../../hooks/useCatalogSectionsDnd';
import { CatalogSectionRow } from './CatalogSectionRow';
import {
  DROPDOWN_MAX_HEIGHT_PX,
  DROPDOWN_WIDTH_PX,
  ROW_GAP_PX,
  ROW_GRIP_OFFSET_PX,
  ROW_HEIGHT_PX,
  ROW_NAME_INDENT_PX,
} from './catalogSectionsDropdown.constants';
import { CatalogSectionsDropdownProps } from './catalogSectionsDropdown.types';
import { useCatalogSectionsDropdown } from './useCatalogSectionsDropdown';

const POPOVER_PAPER_SX = (theme: Theme) => ({
  width: `min(${DROPDOWN_WIDTH_PX}px, calc(100vw - 32px))`,
  maxHeight: `${DROPDOWN_MAX_HEIGHT_PX}px`,
  padding: 0,
  gap: theme.spacing(0.5),
});

export const CatalogSectionsDropdown = ({
  anchorEl,
  catalogId,
  canManage,
  sections,
  onClose,
  onSelectSection,
}: CatalogSectionsDropdownProps) => {
  const { translate } = useTranslation();
  const { mutate: applySections, isPending } = useBatchUpdateCatalogSections({ catalogId });
  const {
    entries,
    editingKey,
    listRef,
    draftName,
    setDraftName,
    isApplyEnabled,
    deletedWithItems,
    isConfirmingDelete,
    cancelDeleteConfirm,
    remove,
    startEdit,
    cancelEdit,
    reorder,
    handleCreate,
    handleCommitEdit,
    handleApply,
  } = useCatalogSectionsDropdown({
    sections,
    applyMutation: { save: applySections, isSaving: isPending },
    onClose,
  });

  const isCreationDimmed = editingKey !== null;
  const orderedKeys = entries.map((entry) => entry.key);
  const { sensors, handleDragEnd } = useCatalogSectionsDnd({
    orderedIds: orderedKeys,
    onReorder: reorder,
  });

  return (
    <ToolbarPopover
      open={anchorEl !== null}
      anchorEl={anchorEl}
      onClose={onClose}
      testId={CatalogProductsTestIds.SECTIONS_DROPDOWN}
      paperSx={POPOVER_PAPER_SX}
    >
      <Box
        sx={(theme) => ({
          padding: theme.spacing(0.75),
          paddingLeft: `${ROW_GRIP_OFFSET_PX}px`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography
          variant="subtitle2"
          noWrap
          testId={CatalogProductsTestIds.SECTIONS_DROPDOWN_COUNT}
        >
          {translate(LangEnum.CATALOGS_SECTIONS_COUNT_LABEL, { count: entries.length })}
        </Typography>
      </Box>

      {canManage && (
        <Box sx={{ paddingX: 1, paddingTop: 0.5 }}>
          <Stack
            direction="row"
            test-id={CatalogProductsTestIds.SECTIONS_DROPDOWN_CREATE_ROW}
            sx={(theme) => ({
              alignItems: 'center',
              gap: 1,
              minHeight: `${ROW_HEIGHT_PX}px`,
              paddingRight: 0.75,
              paddingLeft: `calc(${theme.spacing(0.75)} + ${ROW_NAME_INDENT_PX}px)`,
              borderRadius: `${theme.shape.borderRadiusMd}px`,
              backgroundColor: theme.palette.action.selected,
              opacity: isCreationDimmed ? 0.5 : 1,
            })}
          >
            <BaseInput
              value={draftName}
              disabled={isCreationDimmed}
              placeholder={translate(LangEnum.CATALOGS_SECTIONS_CREATE_PLACEHOLDER)}
              testId={CatalogProductsTestIds.SECTIONS_DROPDOWN_CREATE_INPUT}
              ariaLabel={translate(LangEnum.CATALOGS_SECTIONS_CREATE)}
              sx={(theme) => ({ flex: 1, minWidth: 0, ...theme.typography.body1 })}
              onChange={(event) => setDraftName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleCreate();
                }
              }}
            />
            <IconButton
              icon={Plus}
              size="medium"
              color="primary"
              sx={(theme) => ({
                color: theme.palette.accent.main,
                '&.Mui-disabled': { color: theme.palette.accent.main },
                '&:hover': { backgroundColor: theme.palette.secondary.dark },
                '&:active': { backgroundColor: theme.palette.custom.border.primary },
              })}
              disabled={isCreationDimmed || draftName.trim().length === 0}
              ariaLabel={translate(LangEnum.CATALOGS_SECTIONS_CREATE_CONFIRM)}
              testId={CatalogProductsTestIds.SECTIONS_DROPDOWN_CREATE_CONFIRM}
              onClick={handleCreate}
            />
          </Stack>
        </Box>
      )}

      <Stack
        ref={listRef}
        test-id={CatalogProductsTestIds.SECTIONS_DROPDOWN_LIST}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', gap: `${ROW_GAP_PX}px`, paddingX: 0.5 }}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={orderedKeys} strategy={verticalListSortingStrategy}>
            {entries.map((entry) => (
              <CatalogSectionRow
                key={entry.key}
                entry={entry}
                isEditing={editingKey === entry.key}
                canManage={canManage}
                onStartEdit={startEdit}
                onCommitEdit={handleCommitEdit}
                onCancelEdit={cancelEdit}
                onRemove={remove}
                onSelect={onSelectSection}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Stack>

      {canManage && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'flex-end',
            padding: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Button
            variant="contained"
            color="primary"
            size="small"
            shape="round"
            disabled={!isApplyEnabled}
            loading={isPending}
            testId={CatalogProductsTestIds.SECTIONS_DROPDOWN_APPLY_BUTTON}
            onClick={handleApply}
          >
            {translate(LangEnum.CATALOGS_SECTIONS_APPLY)}
          </Button>
        </Box>
      )}

      <Modal
        isOpen={isConfirmingDelete}
        size="standard"
        header={{ title: LangEnum.CATALOGS_SECTIONS_DELETE_CONFIRM_TITLE }}
        onClose={cancelDeleteConfirm}
        footer={{
          primaryAction: {
            label: LangEnum.GLOBAL_CONFIRM_LABEL,
            isLoading: isPending,
            onClick: handleApply,
          },
          secondaryAction: {
            label: LangEnum.GLOBAL_CANCEL_LABEL,
            onClick: cancelDeleteConfirm,
          },
        }}
      >
        <Stack sx={{ gap: 1 }}>
          <Typography
            variant="body2"
            testId={CatalogProductsTestIds.SECTIONS_DROPDOWN_DELETE_CONFIRM}
          >
            {translate(LangEnum.CATALOGS_SECTIONS_DELETE_CONFIRM_MESSAGE)}
          </Typography>
          <Typography variant="body2">
            {deletedWithItems.map((section) => section.catalogSectionName).join(', ')}
          </Typography>
        </Stack>
      </Modal>
    </ToolbarPopover>
  );
};
